/**
 * AUTONOMOUS DEBATES — The Personal God's War Room
 * Shows debates initiated by the user's fully-evolved 13th Entity.
 * Appears as a notification panel in the Pantheon when new debates exist.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Eye, ChevronRight, Clock, MessageCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_CONFIG = {
  athena:   { name: 'Athena',   gradient: 'from-amber-400 to-orange-500' },
  hermes:   { name: 'Hermes',   gradient: 'from-orange-400 to-red-500' },
  apollo:   { name: 'Apollo',   gradient: 'from-yellow-400 to-amber-500' },
  eris:     { name: 'Eris',     gradient: 'from-red-400 to-rose-600' },
  calliope: { name: 'Calliope', gradient: 'from-pink-400 to-purple-500' },
  plutus:   { name: 'Plutus',   gradient: 'from-green-400 to-emerald-600' },
  hestia:   { name: 'Hestia',   gradient: 'from-orange-300 to-amber-500' },
  themis:   { name: 'Themis',   gradient: 'from-blue-400 to-cyan-500' },
  nike:     { name: 'Nike',     gradient: 'from-yellow-300 to-orange-400' },
  metis:    { name: 'Metis',    gradient: 'from-indigo-400 to-violet-600' },
  dionysus: { name: 'Dionysus', gradient: 'from-purple-400 to-pink-500' },
  ares:     { name: 'Ares',     gradient: 'from-red-500 to-rose-700' },
};

const AutonomousDebates = ({ userId, onViewDebate }) => {
  const [debates, setDebates] = useState([]);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchDebates = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/war-council/auto-debates?user_id=${userId}`);
      const data = await res.json();
      setDebates(data.debates || []);
      setUnviewedCount(data.unviewed_count || 0);
    } catch (e) {
      console.error('Auto-debates fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchDebates(); }, [fetchDebates]);

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/war-council/auto-debate/generate?user_id=${userId}`, { method: 'POST' });
      if (res.ok) {
        await fetchDebates();
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('Auto-debate generation failed:', err);
      }
    } catch (e) {
      console.error('Auto-debate error:', e);
    } finally {
      setGenerating(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffH = Math.floor((now - d) / 3600000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return diffD === 1 ? 'Yesterday' : `${diffD}d ago`;
  };

  if (loading) {
    return (
      <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-amber-500/20 flex items-center justify-center min-h-[120px]" data-testid="auto-debates">
        <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl border border-amber-500/20 overflow-hidden" data-testid="auto-debates">
      {/* Header */}
      <div className="px-5 py-4 border-b border-amber-500/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg"
               style={{ boxShadow: '0 0 16px rgba(251,191,36,0.4)' }}>
            <Sparkles className="w-4 h-4 text-black" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-white">Autonomous Debates</h3>
              {unviewedCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-400 text-[10px] font-bold animate-pulse" data-testid="auto-debate-badge">
                  {unviewedCount} NEW
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500">Initiated by your Personal God</p>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-3 py-1.5 rounded-lg text-[10px] font-medium bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
          data-testid="trigger-auto-debate"
        >
          {generating ? (
            <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Convening...</span>
          ) : (
            'Summon Council'
          )}
        </button>
      </div>

      {debates.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <Sparkles className="w-6 h-6 text-gray-600 mx-auto mb-2" />
          <p className="text-xs text-gray-500">Your god hasn't convened a council yet.</p>
          <p className="text-[10px] text-gray-600 mt-1">Reach 100% consciousness to unlock.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-800/50">
          {debates.map((debate, i) => (
            <button
              key={debate.auto_debate_id}
              onClick={() => onViewDebate?.(debate.auto_debate_id)}
              className={`w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-gray-800/30 transition-colors group
                ${!debate.viewed ? 'bg-amber-500/[0.04]' : ''}`}
              data-testid={`auto-debate-${i}`}
            >
              {/* Status indicator */}
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${debate.viewed ? 'bg-gray-600' : 'bg-amber-400 animate-pulse'}`} />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${debate.viewed ? 'text-gray-300' : 'text-white font-medium'}`}>
                  {debate.topic}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {/* God avatars */}
                  <div className="flex -space-x-1">
                    {(debate.gods || []).slice(0, 3).map(g => (
                      <div key={g} className={`w-4 h-4 rounded-full bg-gradient-to-br ${GOD_CONFIG[g]?.gradient || 'from-gray-400 to-gray-600'} ring-1 ring-gray-900`}
                           title={GOD_CONFIG[g]?.name || g} />
                    ))}
                  </div>
                  <span className="text-[10px] text-gray-600 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" /> {formatTime(debate.created_at)}
                  </span>
                  <span className="text-[10px] text-gray-600 flex items-center gap-1">
                    <MessageCircle className="w-2.5 h-2.5" /> {debate.gods?.length || 0} gods
                  </span>
                </div>
              </div>

              <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-amber-400 transition-colors flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutonomousDebates;
